import React, { useState } from 'react'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import { Typography, Tooltip, Menu, MenuItem, Box } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import WarningIcon from '@material-ui/icons/Warning'
import { useTimezone, TimezoneType } from '../../contexts/timezone/timezone'
import { Address } from '../../models/types'

// Map of common timezone IDs to their 3-letter abbreviations
const timezoneAbbreviations: Record<string, string> = {
    'America/New_York': 'EST',
    'America/Chicago': 'CST',
    'America/Denver': 'MST',
    'America/Los_Angeles': 'PST',
    'America/Phoenix': 'MST',
    'America/Anchorage': 'AKT',
    'Pacific/Honolulu': 'HST',
    'America/Halifax': 'AST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Berlin': 'CET',
    'Europe/Athens': 'EET',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Kolkata': 'IST',
    'Australia/Sydney': 'AEST',
    UTC: 'UTC',
}

interface EnhancedTimezoneSelectorProps {
    propertyAddress?: Address
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        compactDisplay: {
            color: theme.palette.primary.contrastText,
            fontSize: '0.8rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            padding: theme.spacing(1),
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        menuItem: {
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
        },
        checkIcon: {
            color: theme.palette.success.main,
            fontSize: '0.85rem',
            marginLeft: theme.spacing(0.5),
        },
        warningIcon: {
            color: theme.palette.warning.main,
            fontSize: '0.85rem',
            marginLeft: theme.spacing(0.5),
        },
        locationIcon: {
            color: theme.palette.primary.main,
            fontSize: '0.85rem',
            marginRight: theme.spacing(0.5),
        },
        debugInfo: {
            color: theme.palette.primary.contrastText,
            fontSize: '0.6rem',
            textAlign: 'center',
            opacity: 0.7,
            marginTop: theme.spacing(0.5),
            maxWidth: '100px',
            wordBreak: 'break-word',
        },
    }),
)

export const EnhancedTimezoneSelector = ({
    propertyAddress,
}: EnhancedTimezoneSelectorProps) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [showDebug, setShowDebug] = useState(false)

    // Use our timezone context
    const {
        currentTimezone,
        timezoneType,
        useUserTimezone,
        usePropertyTimezone,
        setPropertyTimezone,
        getTimezoneAbbr,
    } = useTimezone()

    // Set property timezone if available
    React.useEffect(() => {
        if (propertyAddress) {
            // Get timezone from property address
            const timezone = getPropertyTimezoneValue()
            setPropertyTimezone(timezone, propertyAddress)
            // No need to call switchToPropertyTimezone() as it happens automatically now
        }
    }, [propertyAddress?.id])

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    // Toggle debug info on long press
    const handleLongPress = () => {
        setShowDebug(!showDebug)
    }

    // Get the correct timezone identifier for the property
    function getPropertyTimezoneValue(): string {
        if (!propertyAddress) return 'UTC'

        // Use the timezone_id from the propertyAddress if available
        // Otherwise fall back to determining from offsets or default to UTC
        if (propertyAddress.timezone_id) {
            return propertyAddress.timezone_id
        }

        // Fallback to UTC if no timezone is specified
        return 'UTC'
    }

    // Get property timezone info
    const propertyTimezoneAbbr = propertyAddress
        ? propertyAddress.timezone_id
            ? getTimezoneAbbr(propertyAddress.timezone_id)
            : 'UTC'
        : 'UTC'

    // Determine if we are currently using property timezone
    const isUsingPropertyTimezone = timezoneType === TimezoneType.PROPERTY

    // Switch to property timezone
    const switchToPropertyTimezone = () => {
        usePropertyTimezone()
        handleClose()
    }

    // Switch to current browser's local timezone
    const switchToLocalTimezone = () => {
        useUserTimezone()
        handleClose()
    }

    // Get tooltip text
    const tooltipTitle = isUsingPropertyTimezone
        ? `Using property timezone (${propertyTimezoneAbbr})`
        : propertyAddress
        ? `⚠️ Using your local timezone (${getTimezoneAbbr(
              currentTimezone,
          )}) instead of property timezone (${propertyTimezoneAbbr})`
        : `Current timezone: ${getTimezoneAbbr(currentTimezone)}`

    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            <Tooltip title={tooltipTitle}>
                <Typography
                    className={classes.compactDisplay}
                    onClick={handleClick}
                    onContextMenu={(e) => {
                        e.preventDefault()
                        handleLongPress()
                    }}
                >
                    {getTimezoneAbbr(currentTimezone)}
                    {isUsingPropertyTimezone && propertyAddress && (
                        <CheckCircleIcon className={classes.checkIcon} />
                    )}
                    {!isUsingPropertyTimezone && propertyAddress && (
                        <WarningIcon className={classes.warningIcon} />
                    )}
                </Typography>
            </Tooltip>

            {/* Show debug info when enabled */}
            {showDebug && propertyAddress && (
                <Box className={classes.debugInfo}>
                    <div>UTC: {propertyAddress.utc_offset / 3600}h</div>
                    <div>DST: {propertyAddress.dst_offset / 3600}h</div>
                    <div>TZ ID: {propertyAddress.timezone_id || 'not set'}</div>
                    <div>
                        TZ Name: {propertyAddress.timezone_name || 'not set'}
                    </div>
                    <div>Current: {currentTimezone}</div>
                    <div>Type: {timezoneType}</div>
                    <div>Match: {isUsingPropertyTimezone ? 'Yes' : 'No'}</div>
                </Box>
            )}

            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                {/* Property timezone option */}
                {propertyAddress && (
                    <MenuItem
                        onClick={switchToPropertyTimezone}
                        className={classes.menuItem}
                        selected={isUsingPropertyTimezone}
                    >
                        <LocationOnIcon className={classes.locationIcon} />
                        Property Timezone ({propertyTimezoneAbbr})
                        {isUsingPropertyTimezone && (
                            <CheckCircleIcon
                                className={classes.checkIcon}
                                style={{ marginLeft: 'auto' }}
                            />
                        )}
                    </MenuItem>
                )}

                {/* Local browser timezone option */}
                <MenuItem
                    onClick={switchToLocalTimezone}
                    className={classes.menuItem}
                    selected={timezoneType === TimezoneType.USER}
                >
                    My Timezone (
                    {getTimezoneAbbr(
                        Intl.DateTimeFormat().resolvedOptions().timeZone,
                    )}
                    )
                    {!isUsingPropertyTimezone && propertyAddress && (
                        <WarningIcon
                            className={classes.warningIcon}
                            style={{ marginLeft: 'auto' }}
                        />
                    )}
                    {timezoneType === TimezoneType.USER && !propertyAddress && (
                        <CheckCircleIcon
                            className={classes.checkIcon}
                            style={{ marginLeft: 'auto' }}
                        />
                    )}
                </MenuItem>
            </Menu>
        </Box>
    )
}
